import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createContext, startTransition, useEffect, useState } from "react";
import posthog from "posthog-js";
import {
  attemptAutoLogin,
  logout,
} from "../utility/httpActions/authenticationActions";
import { useLocation, useNavigate } from "react-router-dom";
import { getRedirectRoute } from "../utility/utilityFunctions";
import { syncLocalStorageCartRequest } from "../utility/httpActions/cartActions";
import { removeLocalStorageTokens } from "../utility/tokenUtils";
import { setLogoutCallback, isSessionRejected } from "../utility/appAxios";
import { frontendRoutes } from "../utility/frontendRoutes";

export const AuthContext = createContext({
  currentUser: undefined,
  setCurrentUser: undefined,
  isLoggedIn: false,
  onLogin: () => {},
  handleLogout: () => {},
  isLoggingOut: false,
  isLoggingIn: false,
  isInitializing: true,
});

const AuthContextProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState();
  // True until the initial "is there a persisted session?" check has run —
  // distinct from isLoggingIn (which only reflects the autoLogin request
  // itself). Route guards need this so they don't render a decision based on
  // isLoggedIn=false before that check has even started; see ProtectedRoute,
  // AdminRoute, and GuestRoute.
  const [isInitializing, setIsInitializing] = useState(true);
  const isLoggedIn = !!currentUser;
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  // Called by the axios interceptor when both tokens are expired mid-session.
  // Does not hit the logout API (token is already dead) — just clears state.
  // Deliberately does not navigate: ProtectedRoute/AdminRoute redirect to login
  // (keeping the page to return to) when the user goes null, so private pages
  // are still covered, while public pages (landing, /roll-credits, product and
  // shop pages) stay put instead of being bounced to /login by a stale session.
  const forceLogout = () => {
    setCurrentUser(null);
    queryClient.clear();
  };
  setLogoutCallback(forceLogout);

  const { mutate: autoLogin, isPending: isLoggingIn } = useMutation({
    mutationFn: attemptAutoLogin,
    onSuccess: (response) => {
      onLogin(response.user, getRedirectRoute(location));
    },
    onError: (error) => {
      // Only clear tokens when the backend actually rejected the session. A
      // network error or 5xx keeps them so the next page load can retry.
      if (isSessionRejected(error)) removeLocalStorageTokens();
    },
    onSettled: () => {
      setIsInitializing(false);
    },
  });

  // Navigates only once the request settles. Navigating while it's still in
  // flight leaves the old session on screen and makes the first click look
  // like it did nothing.
  //
  // Clearing the user and navigating share one startTransition because React
  // Router v7 applies location changes as transitions. A plain
  // setCurrentUser(null) would render first, at the old (protected) URL, and
  // ProtectedRoute's <Navigate to="/login"> would win over our redirect.
  const { mutate: handleLogout, isPending: isLoggingOut } = useMutation({
    mutationFn: logout,
    onSettled: () => {
      removeLocalStorageTokens();
      posthog.reset();
      startTransition(() => {
        setCurrentUser(null);
        navigate(frontendRoutes.HOME, { replace: true });
      });
      queryClient.clear();
    },
  });

  const { mutate: syncLocalStorageCart } = useMutation({
    mutationFn: syncLocalStorageCartRequest,
    onSuccess: () => {
      localStorage.removeItem("cart");
      localStorage.removeItem("hasUnsyncedCartChanges");
      queryClient.invalidateQueries({ queryKey: ["userCart"] });
    },
  });

  useEffect(() => {
    const token = localStorage.getItem("accessToken");
    if (!isLoggedIn && token) {
      autoLogin();
    } else {
      setIsInitializing(false);
    }
  }, [isLoggedIn, autoLogin]);

  const onLogin = (loggedInUser, reroutePath) => {
    setCurrentUser(loggedInUser);
    syncLocalStorageCart();
    const userId = loggedInUser?.id || loggedInUser?._id;
    if (userId) {
      posthog.identify(String(userId), {
        name: loggedInUser.name,
        email: loggedInUser.email,
      });
    }
    if (reroutePath) {
      navigate(reroutePath, { replace: true, state: { from: location } });
    }
  };

  return (
    <AuthContext.Provider
      value={{
        currentUser,
        setCurrentUser,
        isLoggedIn,
        onLogin,
        handleLogout,
        isLoggingOut,
        isLoggingIn,
        isInitializing,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContextProvider;
